import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import PerfectScrollbar from 'react-perfect-scrollbar';
import HelpIcon from '@material-ui/icons/Help';
import ChevronRightRoundedIcon from '@material-ui/icons/ChevronRightRounded';

import {
  calculateConversion,
  dollarAmountsFloat,
  formatAvg,
  formatViews,
  searchResultFormatViews,
} from 'src/helpers/DollarAmounts';
import { useStyles } from './styles';
import { IReportsData } from 'src/web/pages/Dashboard';
import { position } from 'native-base/lib/typescript/theme/styled-system';

type Props = {
  trips: IDashboardTripExtended[];
  reports: IReportsData;
};

export const DashboardTable = ({ trips, reports }: Props) => {
  const classes = useStyles();
  const [selectedTrip, setSelectedTrip] = useState<string[]>([]);

  const sortedTrips = [...trips].sort((a, b) => {
    if (a.total !== b.total) {
      return b.total - a.total; // Sort by total in descending order
    }
    if (a.count !== b.count) {
      return b.count - a.count; // If totals are equal, sort by count in descending order
    }
    const aView = reports[a.id]?.view || 0;
    const bView = reports[b.id]?.view || 0;
    if (aView !== bView) {
      return bView - aView;
    }
    const aSearchResultImpressions =
      (reports[a.id]?.list_view || 0) + (reports[a.id]?.promotion_view || 0);
    const bSearchResultImpressions =
      (reports[b.id]?.list_view || 0) + (reports[b.id]?.promotion_view || 0);
    return bSearchResultImpressions - aSearchResultImpressions;
  });

  return (
    <TableContainer className={classes.dashboardTable}>
      <PerfectScrollbar style={{ maxHeight: 420 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow className={classes.dashboardTableHeadRow}>
              <TableCell>Trip name</TableCell>
              <TableCell className={classes.customRow} align="left">
                <Tooltip title="">
                  <Box position="relative" pr={1}>
                    Listing Views{' '}
                    {/* <HelpIcon
                      style={{
                        fontSize: '12px',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                      }}
                    /> */}
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell className={classes.customRow} align="left">
                <Tooltip title="">
                  <Box position="relative" pr={1}>
                    Avg List Position{' '}
                    {/* <HelpIcon
                      style={{
                        fontSize: '12px',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                      }}
                    /> */}
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell className={classes.customRow} align="left">
                <Tooltip title="">
                  <Box position="relative" pr={1}>
                    Search Result Impressions{' '}
                    {/* <HelpIcon
                      style={{
                        fontSize: '12px',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                      }}
                    /> */}
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell className={classes.customRow} align="left">
                Orders
              </TableCell>
              <TableCell className={classes.customRow} align="left">
                <Tooltip title="">
                  <Box position="relative" pr={1}>
                    Conversion Rate{' '}
                    {/* <HelpIcon
                      style={{
                        fontSize: '12px',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                      }}
                    /> */}
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell className={classes.customRow} align="left">
                Total Earnings
              </TableCell>
              {/* <TableCell>Percent</TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTrips.map((trip) => (
              <React.Fragment key={trip.id}>
                <TableRow className={classes.dashboardTableBodyRow}>
                  <TableCell
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    <Box
                      onClick={() =>
                        setSelectedTrip((prev) =>
                          prev.includes(trip.id)
                            ? prev.filter((id) => id !== trip.id)
                            : [...prev, trip.id]
                        )
                      }
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: '#555',
                        marginRight: '8px',
                        flex: 'none',
                        transition: 'transform 0.3s ease',
                      }}>
                      <ChevronRightRoundedIcon
                        style={{
                          ...(selectedTrip.includes(trip.id) && {
                            transform: 'rotate(90deg)',
                          }),
                        }}
                      />
                    </Box>
                    <Box
                      className={classes.dashboardTableTripColor}
                      style={{ backgroundColor: trip.color, flex: 'none' }}
                    />
                    {trip.name}
                  </TableCell>
                  <TableCell className={classes.customRow}>
                    {formatViews(reports[trip.id]?.view)}
                  </TableCell>
                  <TableCell className={classes.customRow}>
                    {formatAvg(reports[trip.id]?.listPositionAvg)}
                  </TableCell>
                  <TableCell className={classes.customRow}>
                    {searchResultFormatViews(
                      reports[trip.id]?.list_view,
                      reports[trip.id]?.promotion_view
                    )}
                  </TableCell>
                  <TableCell className={classes.customRow}>
                    {trip.count}
                  </TableCell>
                  <TableCell className={classes.customRow}>
                    {calculateConversion(reports[trip.id]?.view, trip.count)}
                  </TableCell>
                  <TableCell className={classes.customRow}>
                    {dollarAmountsFloat(trip.total)}
                  </TableCell>
                  {/* <TableCell>
                  {trip.percent ? `${trip.percent}%` : '0%'}
                </TableCell> */}
                </TableRow>

                {selectedTrip.includes(trip.id) && (
                  <TableRow className={classes.dashboardTableBodyRowFeatured}>
                    <TableCell
                      style={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        style={{
                          padding: '2px 4px',
                          border: '1px solid #0DD1C5',
                          background: 'rgba(13, 209, 197, 0.1)',
                          borderRadius: '2px',
                          marginLeft: '32px',
                        }}>
                        Featured Placement
                      </Box>
                    </TableCell>
                    <TableCell className={classes.customRow}>
                      {formatViews(reports[trip.id]?.promotion_select)}
                    </TableCell>
                    <TableCell className={classes.customRow}>-</TableCell>
                    <TableCell className={classes.customRow}>
                      {formatViews(reports[trip.id]?.promotion_view)}
                    </TableCell>
                    <TableCell className={classes.customRow}>
                      {reports[trip.id]?.promotion_purchase}
                    </TableCell>
                    <TableCell className={classes.customRow}>
                      {calculateConversion(
                        reports[trip.id]?.promotion_select,
                        reports[trip.id]?.promotion_purchase
                      )}
                    </TableCell>
                    <TableCell className={classes.customRow}>
                      {dollarAmountsFloat(
                        +(reports[trip.id]?.promotion_purchase_total || 0),
                        true
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </PerfectScrollbar>
    </TableContainer>
  );
};
